import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';

/**
 * Reusable confirmation dialog shown when a user tries to navigate away from
 * a page with unsaved changes.
 *
 * Props:
 *   open      {boolean}   – whether the dialog is visible
 *   onDiscard {function}  – called when user confirms leaving (blocker.proceed)
 *   onCancel  {function}  – called when user stays (blocker.reset)
 */
function UnsavedChangesDialog({ open, onDiscard, onCancel }) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Gradient header — matches the app's other confirmation dialogs */}
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 2,
        }}
      >
        <WarningAmberIcon sx={{ fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
          {t('unsaved_changes')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ mt: 2, pb: 1 }}>
        <Typography variant="body1" color="text.secondary" lineHeight={1.6}>
          {t('unsaved_changes_message')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          color="inherit"
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {t('stay_on_page')}
        </Button>
        <Button
          onClick={onDiscard}
          variant="contained"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #d97706 0%, #dc2626 100%)',
            },
          }}
        >
          {t('discard_and_leave')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UnsavedChangesDialog;
